/** SUNTRONIX ONE — Master Client Portfolio dashboard (§93). */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import Section from "@/components/app/Section";
import { CheckCircle2, AlertTriangle, Building2, Landmark, Apple, MapPin } from "lucide-react";

export default function MasterClientPortfolio() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/master-clients/portfolio/summary");
      setData(data);
    } catch (e) { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="p-6 text-sm text-slate-500" data-testid="portfolio-loading">Loading portfolio…</div>;

  const filtered = (data.customers || []).filter((c) => {
    if (filter === "gaps") return c.missing?.length > 0;
    if (filter === "tax") return c.missing?.some((m) => m.includes("Tax"));
    if (filter === "abm") return c.missing?.some((m) => m.includes("ABM"));
    if (filter === "addr") return c.missing?.some((m) => m.includes("Registered address verification"));
    return true;
  });

  return (
    <div className="max-w-[1400px] mx-auto space-y-4" data-testid="master-portfolio">
      <PageHeader eyebrow="Master client · Portfolio"
        title="Customer Master health" context="Aggregate view of profile completeness across all customers."
        testId="portfolio-header" />

      <Section title="Overview" testId="portfolio-kpis">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Kpi label="Total customers" value={data.total} testId="pk-total" filter={() => setFilter("all")} />
          <Kpi label="Complete" value={data.complete} tone="ok" testId="pk-complete" filter={() => setFilter("all")}
            icon={CheckCircle2} />
          <Kpi label="Profile gaps" value={data.profile_gaps} tone="warn" testId="pk-gaps" filter={() => setFilter("gaps")}
            icon={AlertTriangle} />
          <Kpi label="Tax missing" value={data.tax_missing} tone="warn" testId="pk-tax" filter={() => setFilter("tax")}
            icon={Landmark} />
          <Kpi label="ABM missing" value={data.abm_missing} tone="warn" testId="pk-abm" filter={() => setFilter("abm")}
            icon={Apple} />
        </div>
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
          <Kpi label="Address verification required" value={data.address_verification_required}
            tone={data.address_verification_required ? "warn" : "ok"}
            testId="pk-addr" filter={() => setFilter("addr")} icon={MapPin} />
        </div>
      </Section>

      <Section title={`Customers · ${filtered.length}`} testId="portfolio-list"
        action={
          <div className="flex gap-1 text-[11px]">
            {[["all","All"],["gaps","Gaps"],["tax","Tax"],["abm","ABM"],["addr","Address"]].map(([k,l]) => (
              <button key={k} onClick={() => setFilter(k)} data-testid={`pf-filter-${k}`}
                className={"px-2 py-1 rounded " + (filter===k?"bg-slate-900 text-white":"text-slate-500 hover:bg-slate-100")}>
                {l}
              </button>
            ))}
          </div>
        }>
        <div className="st-panel divide-y divide-[color:var(--st-border)]">
          {filtered.length === 0 && (
            <div className="p-4 text-[12px] text-slate-500 text-center">No customers match this filter.</div>
          )}
          {filtered.map((c) => (
            <Link key={c.id} to={`/master-clients/${c.id}`}
              className="p-3 flex items-start gap-3 hover:bg-slate-50 text-[12px]" data-testid={`pf-row-${c.id}`}>
              <Building2 className="h-4 w-4 text-slate-400 mt-0.5" strokeWidth={1.6} />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-800">{c.name}</div>
                <div className="text-slate-500">{c.country || "—"} · {c.active ? "Active" : "Draft"}</div>
                {c.missing?.length > 0 && (
                  <div className="mt-1 text-amber-700 text-[11px]">
                    Missing: {c.missing.join(" · ")}
                  </div>
                )}
              </div>
              <div className={"px-2 py-0.5 rounded border text-[11px] " + (
                c.score >= 95 ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                c.score >= 60 ? "bg-amber-50 text-amber-800 border-amber-200" :
                "bg-red-50 text-red-800 border-red-200"
              )}>{c.score}%</div>
            </Link>
          ))}
        </div>
      </Section>
    </div>
  );
}

function Kpi({ label, value, tone, testId, filter, icon: Icon }) {
  const tint = tone === "ok" ? "text-emerald-700" : tone === "warn" ? "text-amber-700" : "text-slate-800";
  return (
    <button onClick={filter} className="st-panel p-3 text-left hover:bg-slate-50" data-testid={testId}>
      <div className="text-[11px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
        {Icon && <Icon className="h-3 w-3" strokeWidth={1.6} />} {label}
      </div>
      <div className={"text-2xl font-heading font-semibold mt-1 " + tint}>{value ?? "—"}</div>
    </button>
  );
}
